import jsPDF from "jspdf";
import "jspdf-autotable";
import moment from "moment";

/**
 * Utilidades para generar reportes PDF de gestión de stock.
 * Usa jsPDF + jspdf-autotable siguiendo el patrón del proyecto.
 */

// ─── Helpers ────────────────────────────────────────────────────────────────

function encabezado(doc, nombreInstitucion, titulo, filtrosTexto) {
  const pageWidth = doc.internal.pageSize.getWidth();

  doc.setFontSize(12);
  doc.text(nombreInstitucion, 14, 15);

  doc.setFontSize(16);
  doc.text(titulo, pageWidth / 2, 25, { align: "center" });

  doc.setFontSize(10);
  let y = 35;
  doc.text(`Fecha de emisión: ${moment().format("DD/MM/YYYY HH:mm")}`, 14, y);
  y += 6;

  if (filtrosTexto) {
    doc.text(filtrosTexto, 14, y);
    y += 6;
  }

  return y;
}

const estilosTabla = {
  theme: "grid",
  headStyles: {
    fillColor: [103, 58, 183],
    textColor: 255,
    fontStyle: "bold",
  },
  styles: { fontSize: 9 },
};

// ─── 1. Listado de Productos ────────────────────────────────────────────────

export function pdfListadoProductos(productos, nombreInstitucion, filtros) {
  const doc = new jsPDF("landscape");
  doc.setProperties({ title: "Listado de Productos" });

  let filtrosTexto = "";
  if (filtros) {
    const partes = [];
    if (filtros.nombre) partes.push(`Nombre: ${filtros.nombre}`);
    if (filtros.codigo) partes.push(`Código: ${filtros.codigo}`);
    if (filtros.estado && filtros.estado !== "0")
      partes.push(`Estado: ${filtros.estado}`);
    if (partes.length) filtrosTexto = partes.join(" | ");
  }

  let y = encabezado(doc, nombreInstitucion, "Listado de Productos", filtrosTexto);

  const tableData = productos.map((p, i) => [
    i + 1,
    p.codigo || "-",
    p.nombre,
    p.stockActual != null ? p.stockActual : "-",
    p.stockMinimo != null ? p.stockMinimo : "-",
    p.puntoReposicion != null ? p.puntoReposicion : "-",
    p.precioCosto != null ? `$${parseFloat(p.precioCosto).toFixed(2)}` : "-",
    p.precioVenta != null ? `$${parseFloat(p.precioVenta).toFixed(2)}` : "-",
    p.unidadMedida || "-",
    p.estado || "-",
  ]);

  doc.autoTable({
    startY: y + 4,
    head: [
      [
        "N°",
        "Código",
        "Nombre",
        "Stock Actual",
        "Stock Mín.",
        "Pto. Repos.",
        "P. Costo",
        "P. Venta",
        "Unidad",
        "Estado",
      ],
    ],
    body: tableData,
    ...estilosTabla,
    columnStyles: {
      0: { cellWidth: 12, halign: "center" },
      1: { cellWidth: 28 },
      2: { cellWidth: 50 },
      3: { cellWidth: 22, halign: "center" },
      4: { cellWidth: 22, halign: "center" },
      5: { cellWidth: 22, halign: "center" },
      6: { cellWidth: 25, halign: "right" },
      7: { cellWidth: 25, halign: "right" },
      8: { cellWidth: 22, halign: "center" },
      9: { cellWidth: 22, halign: "center" },
    },
  });

  // Resumen
  const finalY = doc.lastAutoTable.finalY + 8;
  doc.setFontSize(10);
  doc.setFont(undefined, "bold");
  doc.text(`Total de productos: ${productos.length}`, 14, finalY);

  window.open(doc.output("bloburl"), "_blank");
}

// ─── 2. Movimientos de Stock por Producto ───────────────────────────────────

export function pdfMovimientosStock(
  movimientos,
  nombreInstitucion,
  nombreProducto,
  stockActual,
  filtros
) {
  const doc = new jsPDF();
  doc.setProperties({ title: `Movimientos - ${nombreProducto}` });

  let filtrosTexto = `Producto: ${nombreProducto}`;
  if (filtros && filtros.fechaDesde)
    filtrosTexto += ` | Desde: ${moment(filtros.fechaDesde).format("DD/MM/YYYY")}`;
  if (filtros && filtros.fechaHasta)
    filtrosTexto += ` | Hasta: ${moment(filtros.fechaHasta).format("DD/MM/YYYY")}`;

  let y = encabezado(
    doc,
    nombreInstitucion,
    "Movimientos de Stock",
    filtrosTexto
  );

  if (stockActual !== null && stockActual !== undefined) {
    doc.text(`Stock actual: ${stockActual}`, 14, y);
    y += 6;
  }

  const tableData = movimientos.map((m, i) => [
    i + 1,
    m.fechaString || "-",
    m.tipo || "-",
    m.tipo === "Egreso" || m.tipo === "Consumo"
      ? `-${Math.abs(m.cantidad)}`
      : `+${Math.abs(m.cantidad)}`,
    m.precioCosto != null ? `$${parseFloat(m.precioCosto).toFixed(2)}` : "-",
    m.observaciones || "-",
    m.usuario || "-",
  ]);

  doc.autoTable({
    startY: y + 4,
    head: [["N°", "Fecha", "Tipo", "Cantidad", "P. Costo", "Observaciones", "Usuario"]],
    body: tableData,
    ...estilosTabla,
    columnStyles: {
      0: { cellWidth: 12, halign: "center" },
      1: { cellWidth: 25, halign: "center" },
      2: { cellWidth: 22, halign: "center" },
      3: { cellWidth: 20, halign: "center" },
      4: { cellWidth: 22, halign: "right" },
      5: { cellWidth: 55 },
      6: { cellWidth: 25 },
    },
  });

  // Totales
  const ingresos = movimientos
    .filter((m) => m.tipo === "Ingreso")
    .reduce((acc, m) => acc + Math.abs(m.cantidad), 0);
  const egresos = movimientos
    .filter((m) => m.tipo === "Egreso" || m.tipo === "Consumo")
    .reduce((acc, m) => acc + Math.abs(m.cantidad), 0);
  const ajustes = movimientos
    .filter((m) => m.tipo === "Ajuste")
    .reduce((acc, m) => acc + m.cantidad, 0);

  let finalY = doc.lastAutoTable.finalY + 8;
  doc.setFontSize(10);
  doc.setFont(undefined, "bold");
  doc.text(`Total ingresos: +${ingresos}`, 14, finalY);
  finalY += 5;
  doc.text(`Total egresos: -${egresos}`, 14, finalY);
  finalY += 5;
  if (ajustes !== 0) {
    doc.text(`Total ajustes: ${ajustes >= 0 ? "+" : ""}${ajustes}`, 14, finalY);
    finalY += 5;
  }
  doc.text(`Movimientos totales: ${movimientos.length}`, 14, finalY);

  window.open(doc.output("bloburl"), "_blank");
}

// ─── 3. Consumos del Turno (reutilizable desde modal) ───────────────────────

export function pdfConsumosTurno(
  consumos,
  nombreInstitucion,
  nombrePaciente
) {
  const doc = new jsPDF();
  doc.setProperties({ title: "Reporte de Consumos" });

  let filtrosTexto = "";
  if (nombrePaciente) filtrosTexto = `Paciente: ${nombrePaciente}`;

  let y = encabezado(doc, nombreInstitucion, "Reporte de Consumos", filtrosTexto);

  const tableData = consumos.map((item, i) => [
    i + 1,
    item.fechaString || "-",
    item.descripcion || "-",
    item.cantidad ? item.cantidad : "-",
    item.totalString || `$${parseFloat(item.total).toFixed(2)}`,
  ]);

  doc.autoTable({
    startY: y + 4,
    head: [["N°", "Fecha", "Descripción", "Cant.", "Importe"]],
    body: tableData,
    ...estilosTabla,
    columnStyles: {
      0: { cellWidth: 12, halign: "center" },
      1: { cellWidth: 28, halign: "center" },
      2: { cellWidth: 90 },
      3: { cellWidth: 18, halign: "center" },
      4: { cellWidth: 30, halign: "right" },
    },
  });

  const total = consumos.reduce((acc, c) => acc + c.total, 0);
  const finalY = doc.lastAutoTable.finalY + 8;
  const pageWidth = doc.internal.pageSize.getWidth();
  doc.setFontSize(11);
  doc.setFont(undefined, "bold");
  doc.text(`Total consumido: $${total.toFixed(2)}`, pageWidth - 14, finalY, {
    align: "right",
  });

  window.open(doc.output("bloburl"), "_blank");
}

// ─── 4. Historial General de Consumos ───────────────────────────────────────

export function pdfHistorialConsumos(
  consumos,
  nombreInstitucion,
  filtros
) {
  const doc = new jsPDF("landscape");
  doc.setProperties({ title: "Historial General de Consumos" });

  let filtrosTexto = "";
  const partes = [];
  if (filtros) {
    if (filtros.fechaDesde)
      partes.push(`Desde: ${moment(filtros.fechaDesde).format("DD/MM/YYYY")}`);
    if (filtros.fechaHasta)
      partes.push(`Hasta: ${moment(filtros.fechaHasta).format("DD/MM/YYYY")}`);
    if (filtros.producto) partes.push(`Producto: ${filtros.producto}`);
    if (filtros.paciente) partes.push(`Paciente: ${filtros.paciente}`);
  }
  if (partes.length) filtrosTexto = partes.join(" | ");

  let y = encabezado(
    doc,
    nombreInstitucion,
    "Historial General de Consumos",
    filtrosTexto
  );

  const tableData = consumos.map((item, i) => [
    i + 1,
    item.fechaString || "-",
    item.paciente || "-",
    item.descripcion || "-",
    item.cantidad ? item.cantidad : "-",
    item.totalString || `$${parseFloat(item.total).toFixed(2)}`,
    item.usuario || "-",
  ]);

  doc.autoTable({
    startY: y + 4,
    head: [["N°", "Fecha", "Paciente", "Descripción", "Cant.", "Importe", "Usuario"]],
    body: tableData,
    ...estilosTabla,
    columnStyles: {
      0: { cellWidth: 12, halign: "center" },
      1: { cellWidth: 28, halign: "center" },
      2: { cellWidth: 50 },
      3: { cellWidth: 70 },
      4: { cellWidth: 18, halign: "center" },
      5: { cellWidth: 30, halign: "right" },
      6: { cellWidth: 30 },
    },
  });

  const total = consumos.reduce((acc, c) => acc + (c.total || 0), 0);
  const finalY = doc.lastAutoTable.finalY + 8;
  const pageWidth = doc.internal.pageSize.getWidth();
  doc.setFontSize(11);
  doc.setFont(undefined, "bold");
  doc.text(
    `Total consumos: ${consumos.length} | Importe total: $${total.toFixed(2)}`,
    pageWidth - 14,
    finalY,
    { align: "right" }
  );

  window.open(doc.output("bloburl"), "_blank");
}

// ─── 5. Productos con Stock Bajo (Alertas de Reposición) ────────────────────

export function pdfAlertasStock(productos, nombreInstitucion) {
  const doc = new jsPDF();
  doc.setProperties({ title: "Alertas de Stock" });

  let y = encabezado(
    doc,
    nombreInstitucion,
    "Productos con Stock Bajo / Alertas de Reposición",
    null
  );

  const tableData = productos.map((p, i) => {
    let estado = "Reponer";
    if (p.stockActual <= 0) estado = "Sin stock";
    else if (p.stockActual <= p.stockMinimo) estado = "Bajo mínimo";

    return [
      i + 1,
      p.codigo || "-",
      p.nombre,
      p.stockActual,
      p.stockMinimo,
      p.puntoReposicion,
      estado,
    ];
  });

  doc.autoTable({
    startY: y + 4,
    head: [
      ["N°", "Código", "Nombre", "Stock Actual", "Stock Mín.", "Pto. Repos.", "Alerta"],
    ],
    body: tableData,
    ...estilosTabla,
    columnStyles: {
      0: { cellWidth: 12, halign: "center" },
      1: { cellWidth: 28 },
      2: { cellWidth: 55 },
      3: { cellWidth: 25, halign: "center" },
      4: { cellWidth: 25, halign: "center" },
      5: { cellWidth: 25, halign: "center" },
      6: { cellWidth: 25, halign: "center" },
    },
    didParseCell: function (data) {
      if (data.section === "body" && data.column.index === 6) {
        const val = data.cell.raw;
        if (val === "Sin stock") {
          data.cell.styles.textColor = [211, 47, 47];
          data.cell.styles.fontStyle = "bold";
        } else if (val === "Bajo mínimo") {
          data.cell.styles.textColor = [230, 81, 0];
          data.cell.styles.fontStyle = "bold";
        } else {
          data.cell.styles.textColor = [245, 124, 0];
        }
      }
    },
  });

  const finalY = doc.lastAutoTable.finalY + 8;
  doc.setFontSize(10);
  doc.setFont(undefined, "bold");
  doc.text(`Total productos con alerta: ${productos.length}`, 14, finalY);

  window.open(doc.output("bloburl"), "_blank");
}
